import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { TextTypeEnum } from 'generated/graphql';
import type {
    PageBuilderModuleMetaProps,
    TextColumnProps,
} from '@modules/page-builder/types';
import RichTextDisplay from '../../common/rich-text/display/RichTextDisplay';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';

interface SingleParagraphModuleProps extends PageBuilderModuleMetaProps {
    column: TextColumnProps;
    /**
     * The text type (paragraph, h3, quote). If not provided, gets it from the
     * column properties.
     */
    textType?: TextTypeEnum;
    isNewDesign?: boolean;
    moduleType?: string;
}

/**
 * A rendered Page Builder module for a single paragraph. Can be a paragraph,
 * heading, or quote.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/single-paragraph-module/SingleParagraphModule.tsx}
 */
const SingleParagraphModule = ({
    column,
    meta,
    textType,
    isNewDesign = false,
    moduleType,
}: SingleParagraphModuleProps) => {
    const derivedTextType = useMemo(
        () => textType ?? column.textType ?? TextTypeEnum.Paragraph,
        [textType, column.textType],
    );

    const clickProxy = useWebsiteTrackingProxy({
        professionalID: meta?.professional?.id,
        professionalTitle: meta?.professional?.title,
        itemType: meta?.itemType,
    });

    const className =
        isNewDesign && moduleType !== 'FeaturedProductModule'
            ? cn(
                  'mx-auto w-full px-4.5 font-display !text-[1.375rem] font-[400] leading-[130%] tracking-[0rem] text-[#161616] md:max-w-225 [&>div]:!text-[1.375rem] [&>div]:!leading-[130%] [&>div]:!tracking-[0rem]',
                  derivedTextType === TextTypeEnum.H3 && 'md:max-w-310',
              )
            : cn(
                  'mx-auto w-full px-4.5 font-display !text-[1.375rem] font-[450] leading-[130%] tracking-[0rem] text-[#161616] md:max-w-225 [&>div]:!text-[1.375rem] [&>div]:!leading-[130%] [&>div]:!tracking-[0rem]',
                  derivedTextType === TextTypeEnum.H3 && 'md:max-w-310',
                  moduleType === 'FeaturedProductModule' &&
                      'mx-0 w-fit md:max-w-138',
              );

    return (
        <div
            className={className}
            onClick={clickProxy}
            onContextMenu={clickProxy}
        >
            <RichTextDisplay contentHTML={column.contentHTML} />
        </div>
    );
};

export default SingleParagraphModule;
